import { useCallback, useMemo } from "react";
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs";

export const FILTER_KEYS = ["decisions", "syzitisi", "court", "diadikasia", "epan"];

const parsers = {
  decisions: parseAsString,
  syzitisi: parseAsString,
  court: parseAsString,
  diadikasia: parseAsString,
  epan: parseAsString,
  q: parseAsString,
  page: parseAsInteger.withDefault(0),
};

// Classic handlers may write "null"/"all" into the URL; treat them as unset.
const normalize = (value) =>
  value === null || value === undefined || value === "" || value === "null" || value === "all"
    ? ""
    : value;

// Αστικά list filters stored in the URL (same keys as the classic page, so
// links like ?court=… or ?epan=pending work in both layouts), plus the
// desktop search `q` and the 0-based `page`.
export default function useCaseFilters() {
  const [state, setState] = useQueryStates(parsers, { history: "replace" });

  const filters = useMemo(
    () => ({
      decisions: normalize(state.decisions),
      syzitisi: normalize(state.syzitisi),
      court: normalize(state.court),
      diadikasia: normalize(state.diadikasia),
      epan: normalize(state.epan),
    }),
    [state.decisions, state.syzitisi, state.court, state.diadikasia, state.epan]
  );

  const activeCount = FILTER_KEYS.filter((k) => filters[k]).length;

  // Any filter/search change goes back to the first page.
  const setFilter = useCallback(
    (key, value) => setState({ [key]: value ? value : null, page: null }),
    [setState]
  );

  const setSearch = useCallback(
    (value) => setState({ q: value ? value : null, page: null }),
    [setState]
  );

  const clearFilters = useCallback(
    () =>
      setState({
        decisions: null,
        syzitisi: null,
        court: null,
        diadikasia: null,
        epan: null,
        page: null,
      }),
    [setState]
  );

  const setPage = useCallback(
    (value) =>
      setState((prev) => {
        const next = typeof value === "function" ? value(prev.page) : value;
        return { page: next > 0 ? next : null };
      }),
    [setState]
  );

  return {
    filters,
    activeCount,
    search: state.q ?? "",
    page: state.page,
    setFilter,
    setSearch,
    clearFilters,
    setPage,
  };
}
